import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { SeverityBadge } from './SeverityBadge';
import { Tooltip } from '../ui/Tooltip';
import ProvenanceTooltip from '../forms/layout/ProvenanceTooltip';
import type { GtBenchmarkRunOption } from '../../types';
import type { FieldConfidence } from '../../types/extraction';

interface DiffItem {
  field: string;
  expected: any;
  actual: any;
  expectedAtRunTime?: any;
  status: 'match' | 'mismatch' | 'missing' | 'extra' | 'both_null';
  severity: number;
  error_type?: string;
  weight?: number;
  in_schema?: boolean;
}

interface SectionConfig {
  label_fr: string;
  order: number;
  icon?: string;
}

type DiffFilterType = 'all' | 'differences' | 'gt_empty' | 'divergent';

interface JsonDiffViewerProps {
  diff: DiffItem[];
  provenance?: Record<string, { snippet: string; page: number }>;
  /** PROD1.9 — confiance par champ de l'analyse sélectionnée (pastilles #645). */
  confidences?: Record<string, FieldConfidence>;
  fieldInstructions?: Record<string, string>;
  fieldSections?: Record<string, string>;
  sections?: Record<string, SectionConfig>;
  onProvenanceClick?: (field: string) => void;
  showOnlyErrors?: boolean;
  onShowOnlyErrorsChange?: (value: boolean) => void;
  collapsedSections?: Set<string>;
  onCollapsedSectionsChange?: (value: Set<string>) => void;
  // Editable mode (GT editor)
  editable?: boolean;
  documentFilename?: string;
  onFieldSave?: (fieldKey: string, newValue: unknown) => Promise<void>;
  /** #579 — Set of field keys with unsaved local drafts (mode brouillon). */
  modifiedFields?: Set<string>;
  /**
   * PROD1.7.5 — Trigger field deletion. Called only for fields routed to the
   * virtual `_gt_only` section (champs présents dans le GT mais hors schéma actif).
   * The caller decides between single-doc and bulk-tenant deletion via the modale.
   */
  onFieldDelete?: (fieldKey: string) => void;
  // Benchmark run selector
  benchmarkRuns?: GtBenchmarkRunOption[] | null;
  benchmarkRunsLoading?: boolean;
  selectedRunId?: string | null;
  onSelectRun?: (runId: string) => void;
  onLoadRuns?: () => void;
  onCloseBenchmark?: () => void;
  /** Which filter buttons to show (default: all) */
  availableFilters?: DiffFilterType[];
}

function formatRunOption(run: GtBenchmarkRunOption): string {
  const date = new Date(run.created_at).toLocaleDateString('fr-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const schema = run.schema_version ? `v${run.schema_version}` : '';
  const tags = run.tags.length > 0 ? `[${run.tags.join(', ')}]` : '';
  return [date, run.model_name, schema, tags].filter(Boolean).join(' \u2014 ');
}

export function JsonDiffViewer({
  diff,
  provenance,
  confidences,
  fieldInstructions,
  fieldSections,
  sections,
  onProvenanceClick,
  showOnlyErrors: externalShowOnlyErrors,
  onShowOnlyErrorsChange,
  collapsedSections: externalCollapsedSections,
  onCollapsedSectionsChange,
  editable = false,
  documentFilename,
  onFieldSave,
  modifiedFields,
  onFieldDelete,
  benchmarkRuns,
  benchmarkRunsLoading,
  selectedRunId,
  onSelectRun,
  onLoadRuns,
  onCloseBenchmark,
  availableFilters,
}: JsonDiffViewerProps) {
  // Use external state if provided, otherwise use internal state
  const [internalShowOnlyErrors, setInternalShowOnlyErrors] = useState(false);
  const showOnlyErrors = externalShowOnlyErrors !== undefined ? externalShowOnlyErrors : internalShowOnlyErrors;
  const setShowOnlyErrors = onShowOnlyErrorsChange || setInternalShowOnlyErrors;

  // Filter state for diff assistance
  const [activeFilter, setActiveFilter] = useState<DiffFilterType>('all');

  const [internalCollapsedSections, setInternalCollapsedSections] = useState<Set<string>>(new Set());
  const collapsedSections = externalCollapsedSections !== undefined ? externalCollapsedSections : internalCollapsedSections;
  const setCollapsedSections = onCollapsedSectionsChange || setInternalCollapsedSections;

  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  // #583 — Tooltips instructions LLM délégués au composant <Tooltip> partagé.

  // Editable state
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingField, setSavingField] = useState<string | null>(null);
  const [fieldBgStates, setFieldBgStates] = useState<Record<string, 'success' | 'error'>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const editInputRef = useRef<HTMLInputElement>(null);
  const successTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Benchmark dropdown state
  const [showBenchmarkDropdown, setShowBenchmarkDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasBenchmarkSelected = selectedRunId !== null && selectedRunId !== undefined;

  // Close dropdown on outside click
  useEffect(() => {
    if (!showBenchmarkDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowBenchmarkDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBenchmarkDropdown]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      for (const timer of Object.values(successTimersRef.current)) {
        clearTimeout(timer);
      }
    };
  }, []);

  // Focus edit input
  useEffect(() => {
    if (editingField && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingField]);

  const _toggleField = (field: string) => {
    const newExpanded = new Set(expandedFields);
    if (newExpanded.has(field)) {
      newExpanded.delete(field);
    } else {
      newExpanded.add(field);
    }
    setExpandedFields(newExpanded);
  };
  void _toggleField;

  const toggleSection = (sectionKey: string) => {
    const newCollapsed = new Set(collapsedSections);
    if (newCollapsed.has(sectionKey)) {
      newCollapsed.delete(sectionKey);
    } else {
      newCollapsed.add(sectionKey);
    }
    setCollapsedSections(newCollapsed);
  };

  // Filter counters (computed once via useMemo)
  const filterCounts = useMemo(() => {
    const isValueEmpty = (v: any) => v === null || v === undefined || v === '';
    let differences = 0;
    let gtEmpty = 0;
    let divergent = 0;

    for (const item of diff) {
      const isDiff = item.status === 'mismatch' || item.status === 'missing' || item.status === 'extra';
      if (isDiff) differences++;
      if (isValueEmpty(item.expected) && !isValueEmpty(item.actual)) gtEmpty++;
      if (!isValueEmpty(item.expected) && !isValueEmpty(item.actual) && String(item.expected) !== String(item.actual)) divergent++;
    }

    return { all: diff.length, differences, gtEmpty, divergent };
  }, [diff]);

  // Reset filter when benchmark is deselected
  useEffect(() => {
    if (!hasBenchmarkSelected) setActiveFilter('all');
  }, [hasBenchmarkSelected]);

  const filteredDiff = useMemo(() => {
    const isValueEmpty = (v: any) => v === null || v === undefined || v === '';

    // Apply the active filter (replaces the old showOnlyErrors checkbox)
    let filtered = diff;
    switch (activeFilter) {
      case 'differences':
        filtered = diff.filter(item => item.status === 'mismatch' || item.status === 'missing' || item.status === 'extra');
        break;
      case 'gt_empty':
        filtered = diff.filter(item => isValueEmpty(item.expected) && !isValueEmpty(item.actual));
        break;
      case 'divergent':
        filtered = diff.filter(item => !isValueEmpty(item.expected) && !isValueEmpty(item.actual) && String(item.expected) !== String(item.actual));
        break;
    }

    // Also apply legacy showOnlyErrors if still active (backward compat with external state)
    if (showOnlyErrors && activeFilter === 'all') {
      filtered = filtered.filter(item => item.status !== 'match' && item.status !== 'both_null');
    }

    return filtered;
  }, [diff, activeFilter, showOnlyErrors]);

  const allSectionKeys = useMemo(() => {
    if (!fieldSections || !sections || Object.keys(sections).length === 0) {
      return ['_all'];
    }

    const keys = new Set<string>();
    diff.forEach(item => {
      const sectionKey = fieldSections[item.field];
      if (sectionKey && sections[sectionKey]) {
        keys.add(sectionKey);
      } else {
        keys.add('_other');
      }
    });

    return Array.from(keys);
  }, [diff, fieldSections, sections]);

  const groupedDiff = useMemo(() => {
    if (!fieldSections || !sections || Object.keys(sections).length === 0) {
      return [{ sectionKey: '_all', sectionLabel: null, fields: filteredDiff }];
    }

    const grouped: Record<string, DiffItem[]> = {};
    const unassigned: DiffItem[] = [];

    filteredDiff.forEach(item => {
      const sectionKey = fieldSections[item.field];
      if (sectionKey && sections[sectionKey]) {
        if (!grouped[sectionKey]) {
          grouped[sectionKey] = [];
        }
        grouped[sectionKey].push(item);
      } else {
        unassigned.push(item);
      }
    });

    const sortedSections = Object.entries(sections)
      .sort((a, b) => a[1].order - b[1].order)
      .map(([key, config]) => ({
        sectionKey: key,
        sectionLabel: config.label_fr,
        sectionIcon: config.icon,
        fields: grouped[key] || []
      }))
      .filter(s => s.fields.length > 0);

    if (unassigned.length > 0) {
      sortedSections.push({
        sectionKey: '_other',
        sectionLabel: 'Autres',
        sectionIcon: undefined,
        fields: unassigned
      });
    }

    return sortedSections;
  }, [filteredDiff, fieldSections, sections]);

  useEffect(() => {
    if (!isInitialized && groupedDiff.length > 0 && externalCollapsedSections === undefined) {
      setCollapsedSections(new Set(groupedDiff.map(g => g.sectionKey)));
      setIsInitialized(true);
    }
  }, [groupedDiff, isInitialized, externalCollapsedSections, setCollapsedSections]);

  const expandAll = () => {
    setExpandedFields(new Set(diff.map(item => item.field)));
    setCollapsedSections(new Set());
  };

  const collapseAll = () => {
    setExpandedFields(new Set());
    setCollapsedSections(new Set(allSectionKeys));
  };

  const _copyToClipboard = (value: any) => {
    navigator.clipboard.writeText(JSON.stringify(value, null, 2));
  };
  void _copyToClipboard;

  const getStatusColor = (status: string, severity: number, weight?: number) => {
    if (weight === 0) return 'bg-gray-50 border-gray-200 opacity-60';
    if (status === 'both_null') return 'bg-slate-50 border-slate-200 opacity-50';
    if (status === 'match') return 'bg-green-50 border-green-200';
    if (severity === 1) return 'bg-yellow-50 border-yellow-200';
    if (severity === 2) return 'bg-orange-50 border-orange-200';
    if (severity === 3) return 'bg-red-50 border-red-200';
    return 'bg-gray-50 border-gray-200';
  };

  const getWeightBadge = (weight?: number) => {
    if (weight === undefined) return null;

    const config = {
      3: { label: 'Critique', color: 'bg-red-100 text-red-800', title: 'Poids 3 — Critique : ce champ compte fortement dans le score (erreur très pénalisée).' },
      2: { label: 'Standard', color: 'bg-green-100 text-green-800', title: 'Poids 2 — Standard : poids normal dans le scoring.' },
      1: { label: 'Mineur', color: 'bg-blue-100 text-blue-800', title: 'Poids 1 — Mineur : champ peu pondéré dans le scoring.' },
      0: { label: 'Exclu', color: 'bg-gray-200 text-gray-600', title: 'Poids 0 — Exclu : ce champ est ignoré par le scoring.' },
    }[weight] || { label: String(weight), color: 'bg-gray-100 text-gray-600', title: `Poids ${weight}` };

    return (
      <span className={`px-2 py-0.5 rounded text-xs ${config.color}`} title={config.title}>
        {config.label}
      </span>
    );
  };

  const renderValue = (value: any): string => {
    if (value === null || value === undefined) {
      return 'null';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const getSectionDiffCount = (fields: DiffItem[]) => {
    return fields.filter(f => f.status !== 'match' && f.status !== 'both_null').length;
  };

  const getSectionErrorCount = (fields: DiffItem[]) => {
    return fields.filter(f =>
      f.status !== 'match' &&
      f.status !== 'both_null' &&
      (f.weight === undefined || f.weight > 0)
    ).length;
  };

  // --- Editable field logic ---
  const startEditing = useCallback((field: string, currentValue: any) => {
    if (savingField) return;
    setEditingField(field);
    setEditValue(currentValue == null ? '' : String(currentValue));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, [savingField]);

  const cancelEditing = useCallback(() => {
    setEditingField(null);
    setEditValue('');
  }, []);

  const saveField = useCallback(async () => {
    if (!editingField || !onFieldSave || savingField) return;

    const newValue = editValue === '' ? null : editValue;
    const field = editingField;

    // Find the original value for this field in the diff
    const originalItem = diff.find(d => d.field === field);
    const originalStr = originalItem?.expected == null ? '' : String(originalItem.expected);
    const newStr = newValue == null ? '' : String(newValue);

    // No change — just close
    if (originalStr === newStr) {
      setEditingField(null);
      return;
    }

    setSavingField(field);
    setEditingField(null);

    try {
      await onFieldSave(field, newValue);
      setFieldBgStates((prev) => ({ ...prev, [field]: 'success' }));
      if (successTimersRef.current[field]) clearTimeout(successTimersRef.current[field]);
      successTimersRef.current[field] = setTimeout(() => {
        setFieldBgStates((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }, 2000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur de sauvegarde';
      setFieldBgStates((prev) => ({ ...prev, [field]: 'error' }));
      setFieldErrors((prev) => ({ ...prev, [field]: message }));
    } finally {
      setSavingField(null);
    }
  }, [editingField, editValue, onFieldSave, savingField, diff]);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveField();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  }, [saveField, cancelEditing]);

  const handleSelectRunInternal = (runId: string) => {
    setShowBenchmarkDropdown(false);
    if (onSelectRun) onSelectRun(runId);
  };

  const handleCloseBenchmarkInternal = () => {
    setShowBenchmarkDropdown(false);
    setShowOnlyErrors(false);
    setActiveFilter('all');
    if (onCloseBenchmark) onCloseBenchmark();
  };

  // Get field background override (save success/error)
  const getFieldBgOverride = (field: string): string | null => {
    const state = fieldBgStates[field];
    if (state === 'success') return 'bg-green-100 border-green-300';
    if (state === 'error') return 'bg-red-100 border-red-300';
    return null;
  };

  // Show diff decorations when diff data is present (benchmark selected in editable mode, or always in readonly mode)
  const hasDiffData = hasBenchmarkSelected || !editable;

  const renderFieldItem = (item: DiffItem, index: number) => {
    const bgOverride = getFieldBgOverride(item.field);
    // En mode éditeur GT (`editable=true`), on neutralise la coloration et les
    // badges de sévérité : cette page sert à corriger la vérité-terrain, pas à
    // afficher le score d'un run (la sévérité décrit la distance GT↔extraction
    // IA, pas la qualité du GT lui-même).
    const showSeverityChrome = !editable && hasDiffData;
    const bgClass = bgOverride || (showSeverityChrome
      ? getStatusColor(item.status, item.severity, item.weight)
      : 'bg-white border-gray-200');
    const isEditing = editingField === item.field;
    const isSaving = savingField === item.field;
    const isModified = modifiedFields?.has(item.field) ?? false;

    return (
      <div
        key={`${item.field}-${index}`}
        className={`border rounded ${bgClass} px-3 py-2 ${isModified ? 'ring-2 ring-amber-400' : ''}`}
        data-diff-status={item.status}
        data-gt-empty={item.expected == null || item.expected === '' ? 'true' : 'false'}
      >
        {/* Field header — structure 3 colonnes (gauche / centre / droite)
            qui réplique le layout "Field values" pour aligner le badge central
            ⓘ "GT modifié depuis ce run" pile au-dessus du bouton flèche ←. */}
        <div className="flex items-center gap-2 mb-1">
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="font-mono text-sm font-medium">{item.field}</span>
            {fieldInstructions && fieldInstructions[item.field] && (
              <Tooltip
                content={fieldInstructions[item.field]}
                heading="Instructions LLM"
                maxWidth="max-w-2xl"
                className="text-blue-600"
                ariaLabel={`Instructions LLM pour ${item.field}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Tooltip>
            )}
            {showSeverityChrome && (
              <SeverityBadge severity={item.severity} showLabel={false} />
            )}
            {hasDiffData && getWeightBadge(item.weight)}
            {isModified && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300"
                title="Modification locale non sauvegardée. Utilisez le bouton « Sauvegarder N modifications » en haut."
              >
                modifié
              </span>
            )}
            {/* PROD1.7.5 — Bouton corbeille pour champs hors schéma actif uniquement */}
            {editable && onFieldDelete && fieldSections && fieldSections[item.field] === '_gt_only' && (
              <button
                type="button"
                onClick={() => onFieldDelete(item.field)}
                title="Supprimer ce champ (de ce document ou de tous les GT)"
                aria-label={`Supprimer le champ ${item.field}`}
                className="ml-1 inline-flex items-center justify-center w-6 h-6 rounded text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                </svg>
              </button>
            )}
          </div>

          {/* Slot central : badge ⓘ "GT modifié depuis ce run" — aligné sur le
              bouton flèche ← de la rangée des valeurs (largeur w-7 = 28px). */}
          <div className="flex-shrink-0 w-7 flex items-center justify-center">
            {(() => {
              if (!hasBenchmarkSelected) return null;
              if (!('expectedAtRunTime' in item)) return null;
              const currentStr = item.expected == null ? '' : JSON.stringify(item.expected);
              const runStr = item.expectedAtRunTime == null ? '' : JSON.stringify(item.expectedAtRunTime);
              if (currentStr === runStr) return null;
              const runIdShort = selectedRunId ? selectedRunId.slice(0, 8) : '?';
              return (
                <Tooltip
                  heading="GT modifié depuis ce run"
                  content={
                    <div>
                      <p className="text-xs">Au moment du run <span className="font-mono">{runIdShort}</span>, le GT était :</p>
                      <pre className="mt-1 text-xs font-mono whitespace-pre-wrap break-all">
                        {item.expectedAtRunTime == null ? '(vide)' : renderValue(item.expectedAtRunTime)}
                      </pre>
                    </div>
                  }
                  maxWidth="max-w-md"
                  align="left"
                  ariaLabel="GT modifié depuis ce run"
                  className="text-yellow-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              );
            })()}
          </div>

          <div className="flex-1 min-w-0 flex items-center gap-2 justify-end">
          {/* PROD1.9 — pastille de confiance unifiée (#645) : affichée pour TOUS
              les champs dès qu'une analyse benchmark est sélectionnée. Couleur =
              confiance du champ ; rouge si pas de snippet de provenance. Clic =
              navigue/surligne dans le PDF (si snippet). Remplace l'ancien
              marqueur p.N + le drapeau ⚠ "pas de provenance" (#580). */}
          {hasBenchmarkSelected && item.actual != null && item.actual !== '' && (
            <span className="flex-shrink-0">
              <ProvenanceTooltip
                snippet={provenance?.[item.field]?.snippet}
                page={provenance?.[item.field]?.page}
                confidence={confidences?.[item.field] ?? null}
                fieldLabel={item.field}
                onClick={
                  provenance?.[item.field] && onProvenanceClick
                    ? () => onProvenanceClick(item.field)
                    : undefined
                }
              />
            </span>
          )}
          </div>
        </div>

        {/* Field values */}
        <div className="flex items-stretch gap-2">
          {/* Expected (GT) — editable or readonly */}
          <div className="flex-1 min-w-0">
            {editable && isEditing ? (
              <input
                autoFocus
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={saveField}
                onKeyDown={handleEditKeyDown}
                autoComplete="off"
                className="text-sm font-mono bg-white p-2 rounded border border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full"
              />
            ) : editable ? (
              <div
                onClick={() => startEditing(item.field, item.expected)}
                className={`text-sm font-mono bg-white p-2 rounded border overflow-x-auto max-h-20 cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors ${isSaving ? 'opacity-50' : ''}`}
                title="Cliquer pour modifier"
              >
                {renderValue(item.expected)}
              </div>
            ) : (
              <pre className="text-sm font-mono bg-white p-2 rounded border overflow-x-auto max-h-20">
                {renderValue(item.expected)}
              </pre>
            )}
          </div>

          {/* #578 — Bouton flèche : copie la valeur IA dans le champ GT en mode édition.
              Affiché même quand actual=null pour permettre de corriger un GT contenant
              une valeur erronée (ex: "0" au lieu de null pour adjustement). */}
          {editable && hasBenchmarkSelected && !isEditing && !isSaving && (
            <button
              type="button"
              onClick={() => startEditing(item.field, item.actual)}
              title="Copier la valeur IA dans le GT"
              aria-label="Copier la valeur IA dans le GT"
              className="flex-shrink-0 self-center w-7 h-7 flex items-center justify-center rounded border border-gray-300 bg-white text-gray-600 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
          )}

          {/* Actual (LLM) — always readonly */}
          <div className="flex-1 min-w-0">
            {editable && !hasBenchmarkSelected ? (
              <pre className="text-sm font-mono bg-gray-50 p-2 rounded border border-gray-200 overflow-x-auto max-h-20 text-gray-400">
                {'\u2014'}
              </pre>
            ) : (
              <pre className="text-sm font-mono bg-white p-2 rounded border overflow-x-auto max-h-20">
                {renderValue(item.actual)}
              </pre>
            )}
          </div>
        </div>

        {/* Error message for save failures */}
        {fieldErrors[item.field] && (
          <p className="text-xs text-red-600 mt-1">{fieldErrors[item.field]}</p>
        )}
      </div>
    );
  };

  // Build the ACTUAL column header label
  const actualHeaderLabel = (() => {
    if (!editable) return '🤖 Extraction IA actuelle';
    if (hasBenchmarkSelected && benchmarkRuns) {
      const run = benchmarkRuns.find(r => r.run_id === selectedRunId);
      return run ? `🤖 ${formatRunOption(run)}` : '🤖 Extraction IA';
    }
    return '🤖 Extractions IA disponibles';
  })();

  return (
    <div>
      {/* Sticky header: filters + column headers */}
      <div className="sticky top-0 z-10 bg-white pb-2 -mx-1 px-1">
        {/* Toggle + counters */}
        <div className="mb-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="flex gap-2">
              <button
                onClick={expandAll}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              >
                ▼ Tout développer
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              >
                ▶ Tout réduire
              </button>
            </div>

            {hasDiffData && (
              <div className="flex gap-1">
                {([
                  { key: 'all' as DiffFilterType, label: 'Tous', count: filterCounts.all },
                  { key: 'differences' as DiffFilterType, label: 'Différences', count: filterCounts.differences },
                  { key: 'gt_empty' as DiffFilterType, label: 'GT vides', count: filterCounts.gtEmpty },
                  { key: 'divergent' as DiffFilterType, label: 'Valeurs divergentes', count: filterCounts.divergent },
                ]).filter(({ key }) => !availableFilters || availableFilters.includes(key))
                .map(({ key, label, count }) => (
                  <button
                    key={key}
                    onClick={() => setActiveFilter(key)}
                    className={`px-3 py-1 text-sm rounded border transition-colors ${
                      activeFilter === key
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {label} ({count})
                  </button>
                ))}
              </div>
            )}
          </div>

          {hasDiffData && (
            <div className="text-sm text-gray-600 flex gap-3">
              <span>
                <span className="font-medium text-red-600">{diff.filter(d => d.status !== 'match' && d.status !== 'both_null' && (d.weight === undefined || d.weight > 0)).length}</span> erreurs
              </span>
              <span>
                <span className="font-medium text-orange-600">{diff.filter(d => d.status !== 'match' && d.status !== 'both_null').length}</span> différences
              </span>
              <span className="text-gray-400">/ {diff.length} champs</span>
            </div>
          )}

          {!hasDiffData && (
            <span className="text-sm text-gray-400">{diff.length} champs</span>
          )}
        </div>

        {/* Diff grid header */}
        <div className="grid grid-cols-2 gap-4">
          <div className="font-semibold text-sm text-gray-700 px-4 py-2 bg-gray-100 border rounded">
            {documentFilename ? `📄 Document source (${documentFilename})` : (editable ? '✏️ EXPECTED (Ground Truth)' : '📋 EXPECTED (Ground Truth)')}
          </div>
          <div className="relative" ref={dropdownRef}>
            <div
              onClick={() => {
                if (onLoadRuns) {
                  if (!benchmarkRuns) onLoadRuns();
                  setShowBenchmarkDropdown(prev => !prev);
                }
              }}
              className={`font-semibold text-sm text-gray-700 px-4 py-2 bg-gray-100 border rounded flex items-center justify-between ${
                onLoadRuns ? 'cursor-pointer hover:bg-gray-200 transition-colors' : ''
              }`}
            >
              <span className="truncate">{actualHeaderLabel}</span>
              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                {hasBenchmarkSelected && onCloseBenchmark && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCloseBenchmarkInternal(); }}
                    className="text-gray-400 hover:text-gray-600 p-0.5"
                    title="Fermer le benchmark"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {onLoadRuns && (
                  <svg className={`w-4 h-4 text-gray-500 transition-transform ${showBenchmarkDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </div>
            </div>

            {/* Benchmark run dropdown */}
            {showBenchmarkDropdown && (
              <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                {benchmarkRunsLoading ? (
                  <div className="px-4 py-3 text-sm text-gray-500">Chargement...</div>
                ) : benchmarkRuns && benchmarkRuns.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500">Aucun benchmark disponible</div>
                ) : benchmarkRuns ? (
                  benchmarkRuns.map((run) => (
                    <button
                      key={run.run_id}
                      onClick={() => handleSelectRunInternal(run.run_id)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-indigo-50 border-b border-gray-100 last:border-b-0 ${
                        selectedRunId === run.run_id ? 'bg-indigo-50 font-medium' : ''
                      }`}
                    >
                      <div>{formatRunOption(run)}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        <span className="font-mono">{run.run_id.slice(0, 8)}</span>
                        {run.f1_score !== null && (
                          <span> — F1: {(run.f1_score * 100).toFixed(1)}%</span>
                        )}
                      </div>
                    </button>
                  ))
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Grouped content */}
      <div className="mt-4 space-y-4">
        {groupedDiff.map(({ sectionKey, sectionLabel, fields }) => (
          <div key={sectionKey}>
            {/* Section header (only if we have sections) */}
            {sectionLabel && (
              <div
                className="flex items-center justify-between px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-t cursor-pointer hover:bg-indigo-100"
                onClick={() => toggleSection(sectionKey)}
              >
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">
                    {collapsedSections.has(sectionKey) ? '▶' : '▼'}
                  </span>
                  <span className="font-semibold text-indigo-800">{sectionLabel}</span>
                  <span className="text-sm text-indigo-600">
                    ({fields.length} champs)
                  </span>
                </div>
                {hasDiffData && (
                  <div className="flex gap-2">
                    {getSectionErrorCount(fields) > 0 && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                        {getSectionErrorCount(fields)} erreur{getSectionErrorCount(fields) > 1 ? 's' : ''}
                      </span>
                    )}
                    {getSectionDiffCount(fields) > getSectionErrorCount(fields) && (
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">
                        {getSectionDiffCount(fields)} différence{getSectionDiffCount(fields) > 1 ? 's' : ''}
                      </span>
                    )}
                    {getSectionDiffCount(fields) === 0 && getSectionErrorCount(fields) === 0 && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                        ✓
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Section fields */}
            {!collapsedSections.has(sectionKey) && (
              <div className={`space-y-2 ${sectionLabel ? 'border-l border-r border-b border-indigo-200 rounded-b p-2' : ''}`}>
                {fields.map((item, idx) => renderFieldItem(item, idx))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* #583 — Tooltip délégué au composant <Tooltip> partagé (rendu inline avec le déclencheur). */}
    </div>
  );
}
